<script lang="ts" setup>
import RendererLayout from '@/presentation/layouts/RendererLayout.vue';
import ToastContainer from '@/presentation/components/common/toast/ToastContainer.vue';
import useSharedStore from '@/presentation/store/shared';
import { setupPushNotifications } from '@/presentation/composables/push-notification/usePushNotifications';

const CACHE_STORAGE_CLEAR_KEY = 'newty-cache-storage-cleared-v2';
const sharedStore = useSharedStore();

async function clearCacheStorageOnce() {
	if (typeof window === 'undefined' || window.localStorage.getItem(CACHE_STORAGE_CLEAR_KEY)) return;

	try {
		window.localStorage.setItem(CACHE_STORAGE_CLEAR_KEY, 'true');

		if ('serviceWorker' in navigator) {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.all(registrations.map((registration) => registration.unregister()));
		}

		if ('caches' in window) {
			const cacheNames = await window.caches.keys();
			await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
		}

		window.location.reload();
	} catch (error) {
		console.warn('Cache storage cleanup failed.', error);
	}
}

void clearCacheStorageOnce();

const normalizeDigitsInNativeInput = (event: Event) => {
	const input = event.target as HTMLInputElement | HTMLTextAreaElement;
	if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) return;
	const normalized = input.value.replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));
	if (normalized === input.value) return;
	input.value = normalized;
	input.dispatchEvent(new Event('input', { bubbles: true }));
};

onMounted(() => document.addEventListener('input', normalizeDigitsInNativeInput, true));
onBeforeUnmount(() => document.removeEventListener('input', normalizeDigitsInNativeInput, true));

watch(
	() => [sharedStore.userInformation.loggedIn, sharedStore.userInformation.userId, sharedStore.userInformation.role],
	async ([loggedIn]) => {
		if (!loggedIn) return;

		try {
			await setupPushNotifications();
		} catch (error) {
			console.warn('Push notification setup failed.', error);
		}
	},
	{ immediate: true },
);
</script>

<template>
	<RendererLayout>
		<RouterView v-slot="{ Component, route }">
			<Transition name="page" mode="out-in">
				<component :is="Component" :key="route.fullPath" />
			</Transition>
		</RouterView>
	</RendererLayout>
	<ToastContainer />
</template>
<style lang="scss">
@use './assets/css/main.css';
</style>
